import type { RatePoint, RateSeries } from "@lib/api/interestRates";

export const RANGES = {
  months_6: 6,
  months_12: 12,
  months_60: 60,
} as const;

export type RangeKey = keyof typeof RANGES;

export const RANGE_KEYS = Object.keys(RANGES) as RangeKey[];

export const DEFAULT_RANGE: RangeKey = "months_6";

export interface Tick {
  position: number;
  label: string;
}

export interface ChartView {
  path: string;
  endY: number;
  ticks: Tick[];
  since: number;
  delta: number | null;
  points: RatePoint[];
  start: number;
  end: number;
  low: number;
  high: number;
}

export const VIEWBOX = { width: 1000, height: 300 } as const;

export const DAY_MS = 24 * 60 * 60 * 1000;

// Anything smaller would let a wobble of a few hundredths fill the whole plot.
const MIN_SPAN = 1;
const PLOT_INSET = 0.06;
// Core returns the last 60 months counted from today, so five years always
// start a day or two late; a longer gap means the change would mislead.
const COVERAGE_GAP_MS = 7 * DAY_MS;

export const xPercent = (time: number, start: number, end: number) =>
  ((time - start) / (end - start)) * 100;

export const yPercent = (rate: number, low: number, high: number) =>
  (PLOT_INSET + ((high - rate) / (high - low)) * (1 - 2 * PLOT_INSET)) * 100;

export const valueAt = (points: RatePoint[], time: number) => {
  let low = 0;
  let high = points.length - 1;

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);

    if (points[middle]![0] <= time) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  return points[low]![1];
};

export const addMonths = (time: number, months: number) => {
  const date = new Date(time);
  const target = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1),
  );
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();

  target.setUTCDate(Math.min(date.getUTCDate(), lastDay));

  return target.getTime();
};

export const windowOf = ({ points, asOf }: RateSeries, months: number) => {
  const start = addMonths(asOf, -months);
  const firstAfter = points.findIndex(([time]) => time > start);
  const from = firstAfter === -1 ? points.length : firstAfter;
  const inForce: RatePoint[] = from > 0 ? [[start, points[from - 1]![1]]] : [];

  return { start, points: [...inForce, ...points.slice(from)] };
};

export const scaleOf = (rates: number[]) => {
  const low = Math.min(...rates);
  const high = Math.max(...rates);

  if (high - low >= MIN_SPAN) {
    return { low, high };
  }

  const middle = (low + high) / 2;

  return { low: middle - MIN_SPAN / 2, high: middle + MIN_SPAN / 2 };
};

const stepPath = (
  points: RatePoint[],
  x: (time: number) => number,
  y: (rate: number) => number,
) => {
  const [first, ...rest] = points;
  const open = `M${x(first![0]).toFixed(1)},${y(first![1]).toFixed(1)}`;

  return `${rest.reduce(
    (path, [time, rate]) =>
      `${path}H${x(time).toFixed(1)}V${y(rate).toFixed(1)}`,
    open,
  )}H${VIEWBOX.width}`;
};

export const ticksOf = (start: number, end: number, locale: string) => {
  const months = (end - start) / (30.44 * 24 * 60 * 60 * 1000);
  const step = months <= 7 ? 1 : months <= 13 ? 3 : 12;
  const monthName = new Intl.DateTimeFormat(locale, {
    month: "short",
    timeZone: "UTC",
  });
  const first = new Date(start);
  const ticks: Tick[] = [];

  for (
    let index = first.getUTCFullYear() * 12 + first.getUTCMonth() + 1;
    ;
    index++
  ) {
    const year = Math.floor(index / 12);
    const month = index % 12;
    const time = Date.UTC(year, month, 1);

    if (time > end) {
      break;
    }

    if (step === 12 ? month === 0 : month % step === 0) {
      const name = monthName.format(time);

      ticks.push({
        position: ((time - start) / (end - start)) * 100,
        label:
          step === 12 ? String(year) : month === 0 ? `${name} ${year}` : name,
      });
    }
  }

  return ticks;
};

export const viewOf = (
  series: RateSeries,
  months: number,
  locale: string,
): ChartView => {
  const window = windowOf(series, months);
  const { low, high } = scaleOf(window.points.map(([, rate]) => rate));
  const x = (time: number) =>
    (xPercent(time, window.start, series.asOf) / 100) * VIEWBOX.width;
  const y = (rate: number) =>
    (yPercent(rate, low, high) / 100) * VIEWBOX.height;

  const [firstTime, firstRate] = window.points[0]!;
  const covered = firstTime - window.start <= COVERAGE_GAP_MS;

  return {
    path: stepPath(window.points, x, y),
    endY: yPercent(series.current, low, high),
    ticks: ticksOf(window.start, series.asOf, locale),
    since: firstTime,
    delta: covered ? series.current - firstRate : null,
    points: window.points,
    start: window.start,
    end: series.asOf,
    low,
    high,
  };
};
